import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { ConsoleDashboard, DashboardWidget } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { Orologio } from '../components/Orologio'
import { Griglia, RIGHE_SCHERMO, SinotticoContext } from '../components/Griglia'
import { useRiquadro } from '../components/widget/graficiEagle'
import { DisegnaWidget, type DefinizioneWidget } from '../components/widget/catalogo'
import { dashboardPredefinita } from '../components/widget/modello'
import { useAsync } from '../hooks/useAsync'
import { useEagle } from '../hooks/useEagle'
import { t } from '../i18n/lingua'

/**
 * Il sinottico: la dashboard a tutto schermo, senza menu, per i monitor a
 * parete. Ruota fra le sezioni al ritmo scelto nella dashboard (frecce per
 * cambiare a mano, spazio per fermare, Esc per uscire). Usa la dashboard
 * indicata (`?d=`), altrimenti quella del tenant, altrimenti la personale.
 */
export default function Sinottico() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const permessi = usePermessiModuli()
  const eagle = useEagle()
  const elenco = useAsync(() => api.dashboards(), [])
  const [sezione, setSezione] = useState(() => Math.max(0, Number(params.get('sezione')) || 0))
  const [area, , altezzaArea] = useRiquadro<HTMLDivElement>(0, 0)
  const [fermo, setFermo] = useState(false)

  const visibile = useMemo(() => (d: DefinizioneWidget) =>
    (!d.modulo || permessi.vede(d.modulo)) && (!d.eagle || eagle), [permessi, eagle])
  // Built once per «who can see what» (see EagleSinottico): a default
  // rebuilt on every render carries new widget ids, and every widget remounts.
  const predefinita = useMemo(() => dashboardPredefinita(visibile), [visibile])
  const dash: ConsoleDashboard | null = useMemo(() => {
    if (!elenco.data) return null
    const mie = elenco.data.dashboards
    const voluta = params.get('d')
    return mie.find((d) => d.id === voluta) ?? mie.find((d) => d.shared) ?? mie.find((d) => !d.shared) ?? predefinita
  }, [elenco.data, params, predefinita])

  useEffect(() => {
    document.body.classList.add('sinottico-attivo')
    return () => document.body.classList.remove('sinottico-attivo')
  }, [])
  useEffect(() => {
    if (!dash || fermo || dash.rotation_s <= 0 || dash.sections.length < 2) return
    const id = window.setInterval(() => setSezione((s) => (s + 1) % dash.sections.length), dash.rotation_s * 1000)
    return () => window.clearInterval(id)
  }, [dash, fermo])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!dash) return
      if (e.key === 'Escape') navigate('/')
      if (e.key === 'ArrowRight') setSezione((s) => (s + 1) % dash.sections.length)
      if (e.key === 'ArrowLeft') setSezione((s) => (s - 1 + dash.sections.length) % dash.sections.length)
      if (e.key === ' ') { e.preventDefault(); setFermo((f) => !f) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dash, navigate])

  if (!dash) return null
  const sez = dash.sections[sezione % dash.sections.length]
  return (
    <div className="sinottico">
      <header className="sinottico__testa">
        <div>
          <h1 className="sinottico__titolo">{dash.name} <span className="muted">· {sez.title}</span></h1>
          <div className="dashboard__sezioni" style={{ marginTop: 6 }}>
            {dash.sections.map((s, i) => (
              <button key={s.id} type="button" className={`dashboard__sezione${i === sezione % dash.sections.length ? ' dashboard__sezione--on' : ''}`} onClick={() => setSezione(i)}>{s.title}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Orologio />
          <button type="button" className="secondary" onClick={() => setFermo(!fermo)} title={t('Spazio per fermare o riprendere la rotazione')}>{fermo ? t('Riprendi') : t('Ferma')}</button>
          <button type="button" className="secondary" onClick={() => { const el = document.documentElement; if (document.fullscreenElement) void document.exitFullscreen?.(); else void el.requestFullscreen?.() }}>{t('Schermo intero')}</button>
          <button type="button" className="secondary" onClick={() => navigate('/')} title={t('Esc per uscire')}>{t('Esci')}</button>
        </div>
      </header>
      <div ref={area} className="sinottico__area">
      <SinotticoContext.Provider value>
      <Griglia<DashboardWidget> widgets={sez.widgets} modifica={false} onCambia={() => undefined}
               libero={sez.layout === 'free'} altezzaRiga={sez.layout === 'free' && altezzaArea > 0 ? Math.floor((altezzaArea - 14 * (RIGHE_SCHERMO - 1)) / RIGHE_SCHERMO) : undefined}
               disegna={(w) => <DisegnaWidget tipo={w.type} options={w.options ?? {}} sinottico />} />
      </SinotticoContext.Provider>
      </div>
    </div>
  )
}
